import { defineStore } from "pinia";
import { ref, computed } from "vue";

export interface Permission {
  _id: string;
  cms_module_id: {
    _id: string;
    title: string;
    key: string;
  } | null;
  is_view: boolean;
  is_create: boolean;
  is_update: boolean;
  is_delete: boolean;
}

export const usePermissionsStore = defineStore("permissions", () => {
  const permissions = ref<Permission[]>(JSON.parse(localStorage.getItem("userPermissions") || "[]"));
  const roleTitle = ref<string>(localStorage.getItem("userRoleTitle") || "");

  /**
   * Set permissions and persist to localStorage
   */
  function setPermissions(title: string, newPermissions: Permission[]) {
    roleTitle.value = title;
    permissions.value = newPermissions;
    
    localStorage.setItem("userRoleTitle", title);
    localStorage.setItem("userPermissions", JSON.stringify(newPermissions));
  }

  /**
   * Clear permissions and remove from localStorage
   */
  function clearPermissions() {
    roleTitle.value = "";
    permissions.value = [];
    localStorage.removeItem("userRoleTitle");
    localStorage.removeItem("userPermissions");
  }

  const isAdmin = computed(() => roleTitle.value.trim().toLowerCase() === "admin");

  /**
   * A computed map for faster lookups.
   * Key format: module_key
   */
  const permissionsMap = computed(() => {
    const map: Record<string, { view: boolean; create: boolean; update: boolean; delete: boolean }> = {};
    
    permissions.value.forEach((p) => {
      const technicalKey = p.cms_module_id?.key;
      const titleKey = p.cms_module_id?.title?.toLowerCase();
      
      const payload = {
        view: !!p.is_view,
        create: !!p.is_create,
        update: !!p.is_update,
        delete: !!p.is_delete,
      };

      if (technicalKey) map[technicalKey] = payload;
      if (titleKey) map[titleKey] = payload;
    });
    
    return map;
  });

  return {
    permissions,
    roleTitle,
    isAdmin,
    permissionsMap,
    setPermissions,
    clearPermissions,
  };
});
